import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { isEqual } from "lodash";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";

import { getPetInsuranceByWorkspaceID, setPetInsuranceArchieve } from "../../Api/Services/PetInsuranceServices.js";
import { getPetNamesByWorkspaceId } from "../../Api/Services/PetServices.js";
import { ReactComponent as ViewIcon } from "../../Assets/Images/viewIcon.svg";
import "./GlobalItemList.css";
import useAuth from '../../ContextProvider/AuthProvider'
import { filterAscDescData, hexToRgba } from "../../Utils.js";
import DetailsModal from "../Common/DetailsModal.js";
import PawItemList from "../Common/PawItemList.js";
import { Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export default function PetInsuarance() {
  const { workspaceId: w_id } = useAuth();
  const [selectedPetName, setSelectedPetName] = useState("");
  const [open, setOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [archiveId, setArchiveId] = useState(null);
  const [filter, setFilter] = useState({});
  const [pageCount, setPageCount] = useState(0);
  const [selInsuranceData, setSelInsuranceData] = useState([]);
  const fetchIdRef = useRef(0);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleArchiveClick = (medication_id) => {
    setShowSuccessMessage(true);
    setOpen(true);
    setArchiveId(medication_id); 
  };
  const handleArchive = () => {
    setIsUpdating(true);

    setPetInsuranceArchieve(archiveId)
      .then((response) => {
        setIsUpdating(false);
        console.log(response);
        if (response.data.success === true) {
          window.location.reload();
        }
      })
      .catch((err) => {
        console.error(err);
        setOpen(true);
        setIsUpdating(false);
      });
  };

  const { data: insuranceData, isLoading: isLoading } = useSWR(
    w_id ? `insuranceData${w_id}` : null,
    async () => {
      const { data } = await getPetInsuranceByWorkspaceID(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchData = useCallback((oFilter) => {
    const fetchId = ++fetchIdRef.current;
    if (fetchId === fetchIdRef.current && !isEqual(filter, oFilter)) {
      setFilter(oFilter);
    }
  }, []);

  useEffect(() => {
    const { limit, from, sort, ...props } = filter;
    if (filter && filter.from > -1) {
      if (insuranceData?.length && limit) {
        setPageCount(Math.ceil((insuranceData?.length) / limit));
        const allData = [...insuranceData];
        const filteredResult = filterAscDescData(allData, sort, from, limit);
        setSelInsuranceData(filteredResult);
      }
    }
  }, [filter, insuranceData]);

  const { data: petNames = [] } = useSWR(
    w_id ? `petNames${w_id}` : null,
    async () => {
      const { data } = await getPetNamesByWorkspaceId(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  let filteredInsuranceData = [];

  if (selectedPetName === "") {
    filteredInsuranceData = selInsuranceData;
  } else if (!selInsuranceData) {
    filteredInsuranceData = [];
  } else {
    filteredInsuranceData = selInsuranceData?.filter((item) => {
      const petName = item?.name?.toLowerCase() || "";
      const query = selectedPetName?.toLowerCase();
      return petName.includes(query);
    });
  }

  const petNamesSet = new Set();
  const filteredPetNames = petNames.filter((pet) => {
    const lowercaseName = pet.name.toLowerCase();
    if (!petNamesSet.has(lowercaseName)) {
      petNamesSet.add(lowercaseName);
      return true;
    }
    return false;
  });

  const defaultOptions = {
    pageSize: 5,
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "",
        accessor: "image",
        width: "8vw",
        disableSortBy: true,
        Cell: ({ row }) => {
          return row?.original?.image ? (
            <img
              alt="petImage"
              src={row?.original?.image}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "10px",
                objectFit: "cover",
                margin: "0 auto",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <AccountCircleIcon
              sx={{
                width: "50px",
                height: "50px",
              }}
            />
          );
        },
      },
      {
        Header: "Pet Name",
        accessor: "name",
        width: "12.5vw",
      },
      {
        Header: `Insurance provider name`,
        accessor: "provider_name",
        width: "12.5vw",
      },
      {
        Header: "Member ID",
        accessor: "member_id",
        width: "12.5vw",
      },
      {
        Header: "",
        accessor: "id",
        width: "8vw",
        isArchive: true,
        disableSortBy: true,
        disabled: isUpdating,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <UnarchiveIcon
              style={{
                color: "#000000",
              }}
              onClick={() =>
                handleArchiveClick(row?.original?.id)
              }
            />
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        width: "8vw",
        disableSortBy: true,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <Button
              sx={{
                minWidth: "40px",
                background: `${hexToRgba(theme.palette.primary.main, 0.20)} !important`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => navigate(props.viewDataLink(row.original))}
            >
              <ViewIcon
                style={{
                  fill: `${theme.palette.primary.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
    ];
  }, []);

  const props = {
    label: "Pet Insurance",
    setSelectedUserName: setSelectedPetName,
    items: filteredPetNames,
    isLoading: isLoading,
    dataAddLink: "/pet-insurance/pet-insurance-form",
    columns,
    columnsData: filteredInsuranceData,
    viewDataLink: (data) => `/pet-insurance/insurance-info/${data?.id}`,
    placeholder:'pet name',
    details: 'There are no pet insurance details for this user.',
    fetchData,
    pageCount,
    defaultOptions,
  };

  const ButtonItem = [
    {
      name: "Yes",
      onClick: () => {
        handleArchive();
        setShowSuccessMessage(false);
      },
    },
    {
      name: "No",
      onClick: () => {
        setOpen(false);
        setShowSuccessMessage(false);
      },
    },
  ];

  return (
    <>
      <PawItemList {...props} />
      {showSuccessMessage && (
        <>
          <DetailsModal
            open={open}
            severity="warning"
            buttonList={ButtonItem}
            title="Do you want to archive this pet insurance?"
            handleClose={()=> setOpen(false)}
          />
        </>
      )}
    </>
  );
}
